/**
 * useKeyboardNav: "Go-to" keyboard shortcuts for dashboard layouts.
 *
 * Press  G  then a letter to navigate instantly:
 *
 *  Librarian          Admin
 *  G D  Dashboard     G O  Overview
 *  G B  Books         G B  Books
 *  G A  Authors       G U  Users
 *  G P  Publishers    G L  Loans
 *  G L  Loans         G I  Inventory
 *  G M  Members       G R  Reports
 *  G T  Tasks         G N  Notifications
 *  G N  Notifications G S  Settings
 *  G S  Settings
 *
 * Press  ?  to open the shortcuts cheatsheet overlay.
 * Press  Escape  to close the cheatsheet.
 *
 * Uses a 1.2-second timeout window: if G is not followed by a valid
 * letter within 1.2 s the sequence is cancelled.
 *
 * Shortcuts are suppressed when focus is inside an <input>, <textarea>,
 * <select>, or a contenteditable element.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface GotoShortcut {
  key: string;
  label: string;
  href: string;
}

const TIMEOUT_MS = 1200;

function isFocusedOnInput(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (['input', 'textarea', 'select'].includes(tag)) return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

interface UseKeyboardNavOptions {
  shortcuts: GotoShortcut[];
}

interface UseKeyboardNavReturn {
  /** Whether the cheatsheet overlay is visible */
  helpOpen: boolean;
  closeHelp: () => void;
}

export function useKeyboardNav({ shortcuts }: UseKeyboardNavOptions): UseKeyboardNavReturn {
  const navigate = useNavigate();
  const [waitingForSecond, setWaitingForSecond] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      /* Skip when typing in a form field */
      if (isFocusedOnInput()) return;

      /* Skip modifier combos (handled by CommandPalette for ⌘K) */
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const key = e.key.toUpperCase();

      /* ? → toggle cheatsheet */
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setHelpOpen((v) => !v);
        return;
      }

      /* Escape → close cheatsheet */
      if (e.key === 'Escape') {
        if (helpOpen) { e.preventDefault(); setHelpOpen(false); }
        return;
      }

      if (!waitingForSecond) {
        /* First stroke: wait for G */
        if (key === 'G') {
          e.preventDefault();
          setWaitingForSecond(true);
          clearTimer();
          timerRef.current = setTimeout(() => {
            setWaitingForSecond(false);
          }, TIMEOUT_MS);
        }
      } else {
        /* Second stroke: resolve shortcut */
        clearTimer();
        setWaitingForSecond(false);
        const match = shortcuts.find((s) => s.key.toUpperCase() === key);
        if (match) {
          e.preventDefault();
          navigate(match.href);
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      clearTimer();
    };
  }, [waitingForSecond, helpOpen, shortcuts, navigate, clearTimer]);

  return { helpOpen, closeHelp: () => setHelpOpen(false) };
}
